import React, { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { formatDateOnly, formatTimestamp, timestampMs } from "@/lib/datetime"
import {
  AlertCircle,
  ChevronLeft,
  CheckCircle2,
  Link as LinkIcon,
  Lock,
  Phone,
  PhoneCall,
  RotateCcw,
} from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/components/ui/use-toast"
import {
  ASSIGNEE_LABEL,
  ActionItemStatusMenu,
  PriorityBadge,
  formatDueDate,
} from "@/components/actionItems/actionItemDisplay"
import { api } from "@/lib/api"
import { ActionItemDetail, ActionItemResponse, ActionItemStatus } from "@/lib/types"
import { cn } from "@/lib/utils"
import { useActionItemsStore } from "@/stores/actionItemsStore"
import { useOrganizationsStore } from "@/stores/organizationsStore"

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; item: ActionItemDetail }
  | { kind: "forbidden" }
  | { kind: "not-found" }
  | { kind: "error" }

/**
 * /action-items/:id — one action item. The urgent action item text links here.
 *
 * The item id alone is enough: the API answers only if the caller is a
 * superuser or a member of the item's organization, and the answer carries
 * organization_id, so we switch the portal to that organization instead of
 * asking. AppLayout keeps the organization picker closed on this route so a
 * user with no organization chosen yet is not asked to pick one first.
 * Signed-out users see the sign-in form at this same URL and land here after.
 */
export default function ActionItemPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const organizations = useOrganizationsStore((s) => s.organizations)
  const updateActionItemStatus = useActionItemsStore((s) => s.updateActionItemStatus)
  const [state, setState] = useState<LoadState>({ kind: "loading" })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setState({ kind: "loading" })
    api
      .getActionItem(id)
      .then((item) => {
        if (cancelled) return
        setState({ kind: "loaded", item })
        // Follow the item into its organization. The API has already checked access.
        const orgs = useOrganizationsStore.getState()
        if (orgs.selectedOrganizationId !== item.organization_id) {
          const hadOrg = !!orgs.selectedOrganizationId
          orgs.setSelectedOrganizationId(item.organization_id)
          const name = orgs.organizations.find((o) => o.id === item.organization_id)?.name
          if (hadOrg) {
            toast({
              title: "Organization switched",
              description: name ? `Now showing ${name}.` : undefined,
            })
          }
        }
      })
      .catch((error) => {
        if (cancelled) return
        const status = error?.response?.status
        // 422 is a malformed id, which is as good as not found.
        if (status === 403) setState({ kind: "forbidden" })
        else if (status === 404 || status === 422) setState({ kind: "not-found" })
        else setState({ kind: "error" })
      })
    return () => {
      cancelled = true
    }
  }, [id, toast])

  const backButton = (
    <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate("/action-items")}>
      <ChevronLeft className="h-4 w-4 mr-1" />
      Back to Action Items
    </Button>
  )

  if (state.kind === "loading") {
    return (
      <div className="mx-auto max-w-4xl min-w-0 space-y-4 p-4 md:p-8">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-[320px] w-full" />
      </div>
    )
  }

  if (state.kind !== "loaded") {
    const message =
      state.kind === "forbidden"
        ? {
            icon: Lock,
            title: "You don't have access to this action item",
            body: "It belongs to an organization your account is not a member of. If you think you should see it, ask your clinic's Pear admin to add you, or sign in with the account that received the text.",
          }
        : state.kind === "not-found"
          ? { icon: AlertCircle, title: "Action item not found", body: "It may have been removed, or the link is incomplete." }
          : { icon: AlertCircle, title: "Couldn't load this action item", body: "Please try refreshing the page." }
    const Icon = message.icon
    return (
      <div className="mx-auto max-w-4xl min-w-0 space-y-4 p-4 md:p-8">
        {backButton}
        <Alert variant={state.kind === "error" ? "destructive" : "default"}>
          <Icon className="h-4 w-4" />
          <AlertTitle>{message.title}</AlertTitle>
          <AlertDescription>{message.body}</AlertDescription>
        </Alert>
      </div>
    )
  }

  const item = state.item
  const isDone = item.status === "completed"
  const orgName = item.organization_name || organizations.find((o) => o.id === item.organization_id)?.name
  const dueInfo = formatDueDate(item.due_date)
  // Status changes bump updated_at; who made them is not recorded.
  const wasUpdated = timestampMs(item.updated_at) - timestampMs(item.created_at) > 60_000
  const patientName = item.patient ? `${item.patient.first_name} ${item.patient.last_name}` : null
  const patientFacts = [
    item.patient?.date_of_birth ? `DOB ${formatDateOnly(item.patient.date_of_birth, "MMM d, yyyy")}` : null,
    item.patient_mrn ? `MRN ${item.patient_mrn}` : null,
  ].filter(Boolean)
  const assignedNames = (item.assigned_users ?? [])
    .map((u: { name?: string | null; email?: string }) => u.name || u.email)
    .filter(Boolean)

  const changeStatus = async (status: ActionItemStatus) => {
    if (status === item.status) return
    setSaving(true)
    const result = await updateActionItemStatus(item.id, { status })
    setSaving(false)
    if (result) {
      // The status endpoint returns the bare item; keep the detail fields we have.
      setState({ kind: "loaded", item: { ...item, ...result, patient: item.patient } })
    } else {
      toast({ title: "Couldn't update status", description: "Please try again.", variant: "destructive" })
    }
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast({ title: "Link copied" })
    } catch {
      toast({ title: "Couldn't copy the link", variant: "destructive" })
    }
  }

  return (
    <div className="mx-auto max-w-4xl min-w-0 space-y-4 p-4 md:space-y-6 md:p-8">
      <div className="space-y-2">
        {backButton}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <PriorityBadge priority={item.priority} />
              <ActionItemStatusMenu status={item.status} onChange={changeStatus} disabled={saving} />
            </div>
            <h1
              className={cn(
                "text-2xl font-semibold tracking-tight break-words",
                isDone && "line-through text-muted-foreground"
              )}
            >
              {item.title}
            </h1>
            {orgName && <p className="text-sm text-muted-foreground">{orgName}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="icon" onClick={copyLink} aria-label="Copy link" title="Copy link">
              <LinkIcon className="h-4 w-4" />
            </Button>
            <Button
              className="flex-1 md:flex-none"
              variant={isDone ? "outline" : "default"}
              disabled={saving}
              onClick={() => changeStatus(isDone ? "pending" : "completed")}
            >
              {isDone ? <RotateCcw className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              {isDone ? "Reopen" : "Mark Complete"}
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-6 p-4 md:p-6">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
            <Field label="Patient">
              {item.patient_id && patientName ? (
                <>
                  <Link
                    to={`/patients/${item.patient_id}?tab=action-items`}
                    className="font-medium text-primary hover:underline"
                  >
                    {patientName}
                  </Link>
                  {patientFacts.length > 0 && (
                    <div className="text-muted-foreground">{patientFacts.join(" · ")}</div>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">General (no patient)</span>
              )}
            </Field>
            {item.patient_phone_number && (
              <Field label="Phone">
                <a
                  href={`tel:${item.patient_phone_number}`}
                  className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {formatPhone(item.patient_phone_number)}
                </a>
              </Field>
            )}
            <Field label="Due">
              {dueInfo ? (
                <span
                  className={cn(
                    dueInfo.isOverdue && "text-destructive font-medium",
                    dueInfo.isUrgent && "text-warning font-medium"
                  )}
                  title={item.due_date ? formatDateOnly(item.due_date, "PPP") || undefined : undefined}
                >
                  {dueInfo.text}
                </span>
              ) : (
                <span className="text-muted-foreground">No due date</span>
              )}
            </Field>
            <Field label="Assignee">
              {ASSIGNEE_LABEL[item.assignee ?? ""] ?? item.assignee ?? "Unassigned"}
              {assignedNames.length > 0 && (
                <span className="text-muted-foreground"> · {assignedNames.join(", ")}</span>
              )}
            </Field>
            <Field label="Created">
              <span title={formatTimestamp(item.created_at, "PPpp")}>{formatTimestamp(item.created_at, "MMM d, yyyy h:mm a")}</span>
              {wasUpdated && (
                <div className="text-muted-foreground" title={formatTimestamp(item.updated_at, "PPpp")}>
                  Updated {formatTimestamp(item.updated_at, "MMM d, h:mm a")}
                </div>
              )}
            </Field>
          </dl>

          {item.description && (
            <section className="space-y-1.5">
              <h2 className="text-sm font-medium text-muted-foreground">Description</h2>
              <p className="whitespace-pre-wrap break-words text-sm">{item.description}</p>
            </section>
          )}

          {item.conversation_snippet && (
            <section className="space-y-1.5">
              <h2 className="text-sm font-medium text-muted-foreground">From the conversation</h2>
              <blockquote className="whitespace-pre-wrap break-words rounded-lg border-l-2 border-primary/40 bg-muted/50 px-4 py-3 text-sm">
                {item.conversation_snippet}
              </blockquote>
            </section>
          )}
        </CardContent>
      </Card>

      {item.call_id && <SourceCall callId={item.call_id} call={item.call ?? null} />}

      {item.patient_id && (
        <OtherOpenItems
          patientId={item.patient_id}
          organizationId={item.organization_id}
          currentId={item.id}
        />
      )}
    </div>
  )
}

function SourceCall({ callId, call }: { callId: string; call: ActionItemDetail["call"] }) {
  const when = call?.started_at || call?.created_at
  const seconds =
    call?.started_at && call?.ended_at
      ? Math.round((timestampMs(call.ended_at) - timestampMs(call.started_at)) / 1000)
      : null
  const facts = [
    call?.call_type === "dialin" ? "Inbound call" : call?.call_type === "dialout" ? "Outbound call" : "Call",
    when ? formatTimestamp(when, "MMM d, h:mm a") || null : null,
    seconds !== null && seconds >= 0 ? formatDuration(seconds) : null,
    call?.call_status && call.call_status !== "completed" ? capitalize(call.call_status.replace(/_/g, " ")) : null,
  ].filter(Boolean)
  return (
    <Card>
      <CardContent className="space-y-3 p-4 md:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-sm font-medium text-muted-foreground">Source call</h2>
            <p className="text-sm">{facts.join(" · ")}</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/calls/${callId}`}>
              <PhoneCall className="mr-2 h-4 w-4" />
              View Call
            </Link>
          </Button>
        </div>
        {call?.notes && (
          <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{call.notes}</p>
        )}
      </CardContent>
    </Card>
  )
}

/** The patient's other open items, so staff can handle them in the same call. */
function OtherOpenItems({
  patientId,
  organizationId,
  currentId,
}: {
  patientId: string
  organizationId: string
  currentId: string
}) {
  const [items, setItems] = useState<ActionItemResponse[]>([])

  useEffect(() => {
    let cancelled = false
    api
      .getPatientActionItems(patientId, organizationId)
      .then((res) => {
        if (!cancelled) setItems(res.items ?? [])
      })
      .catch(() => {
        // Optional context; the page is complete without it.
      })
    return () => {
      cancelled = true
    }
  }, [patientId, organizationId])

  const open = items
    .filter((i) => i.id !== currentId && i.status !== "completed" && i.status !== "cancelled")
    .sort((a, b) => (PRIORITY_RANK[a.priority] ?? 3) - (PRIORITY_RANK[b.priority] ?? 3))
  if (open.length === 0) return null
  const shown = open.slice(0, MAX_OTHER_ITEMS)

  return (
    <Card>
      <CardContent className="space-y-2 p-4 md:p-6">
        <h2 className="text-sm font-medium text-muted-foreground">
          Other open items for this patient ({open.length})
        </h2>
        <ul className="divide-y">
          {shown.map((other) => {
            const due = formatDueDate(other.due_date)
            return (
              <li key={other.id}>
                <Link
                  to={`/action-items/${other.id}`}
                  className="flex items-center gap-3 py-2 text-sm hover:text-primary"
                >
                  <PriorityBadge priority={other.priority} />
                  <span className="min-w-0 flex-1 truncate">{other.title}</span>
                  {due && (
                    <span className={cn("shrink-0 text-xs text-muted-foreground", due.isOverdue && "text-destructive")}>
                      Due {due.text}
                    </span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
        {open.length > shown.length && (
          <Link to={`/patients/${patientId}?tab=action-items`} className="text-sm text-primary hover:underline">
            See all on the patient page
          </Link>
        )}
      </CardContent>
    </Card>
  )
}

const MAX_OTHER_ITEMS = 5
const PRIORITY_RANK: Record<string, number> = { URGENT: 0, MEDIUM: 1, LOW: 2 }

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

/** +12815550100 -> (281) 555-0100; anything else is shown as stored. */
const formatPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, "")
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits
  return local.length === 10 ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}` : phone
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="break-words">{children}</dd>
    </div>
  )
}
